import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import notificationSound from "./Assets/Notification Sound.mpeg";

const GlobalContext = createContext();

export function GlobalProvider({ children }) {
  const baseurl = process.env.REACT_APP_SALES_API_BASE_URL;
  const username = process.env.REACT_APP_API_AUTH_USERNAME;

  const password = process.env.REACT_APP_API_AUTH_PASSWORD;

  const appName = process.env.REACT_APP_APP_NAME;
  const apiGraphqlUrl = process.env.ReACT_APP_API_AUTH_GRAPHQL_URL;
  const GraphToken = process.env.REACT_APP_API_AUTH_GRAPHQL_API_KEY;

const packkyBumppyBaseUrl =
  process.env.REACT_APP_PACKKY_BUMPPY_API_BASE_URL;

  const basicAuthBumppyCredentials =
  process.env.REACT_APP_BASIC_AUTH_BUMPPY_CREDENTIALS || "";

const [bumppyUsername, ...bumppyPasswordParts] =
  basicAuthBumppyCredentials.trim().split(":");

const bumppyPassword = bumppyPasswordParts.join(":");

  const audioRef = useRef(new Audio(notificationSound));
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = 0.6;
    }
  }, []);

  const [AdminId, setAdminId] = useState(() => {
    const adminSaved = sessionStorage.getItem("admin");
    const volunteerSaved = sessionStorage.getItem("volunteerData");

    if (adminSaved) {
      const parsed = JSON.parse(adminSaved);
      // console.log(" Admin userId:", parsed.userId);
      return parsed.userId;
    }

    if (volunteerSaved) {
      const parsed = JSON.parse(volunteerSaved);
      console.log(" Volunteer userId:", parsed.userId);
      return parsed.userId;
    }

    // console.log(" No AdminId found");
    return null;
  });

  const [accessToken, setAccessToken] = useState(() => {
    const adminSaved = sessionStorage.getItem("admin");
    if (adminSaved) return JSON.parse(adminSaved).accessToken;

    const volunteerSaved = sessionStorage.getItem("volunteerData");
    if (volunteerSaved) return JSON.parse(volunteerSaved).accessToken;

    return null;
  });

  const [admin, setAdmin] = useState(() => {
    const saved = sessionStorage.getItem("admin");
    return saved ? JSON.parse(saved) : null;
  });

  const [showOld, setShowOld] = useState(() => {
    const savedView = localStorage.getItem("viewMode");
    return savedView === "task";
  });

  useEffect(() => {
    localStorage.setItem("viewMode", showOld ? "task" : "map");
  }, [showOld]);

  const [isMuted, setIsMuted] = useState(() => {
    const savedMute = localStorage.getItem("notificationMuted");
    return savedMute === "true";
  });

  const isMutedRef = useRef(isMuted);

useEffect(() => {
  isMutedRef.current = isMuted;
}, [isMuted]);

  const toggleMute = () => {
    setIsMuted((prev) => {
      localStorage.setItem("notificationMuted", !prev);
      return !prev;
    });
  };

  const [ApiCall, setApiCall] = useState(false);
  const [IsUnauthorised, setIsUnauthorised] = useState(false);
  const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);

  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState("");

  const handleApiResponseCode = useCallback(
    (data) => {
      const responseCode = data?.responseCode;
      const responseMessage = data?.responseMessage;

      if (responseCode === "98" || responseCode === "401") {
        setIsUnauthorised(true);
        return true;
      }

      if (responseCode === "03") {
        setAccessDeniedMessage(responseMessage || "Access Denied");
        setIsAccessDenied(true);
        return true;
      }

      return false;
    },
    [setIsUnauthorised],
  );

  // Track if admin data has been loaded from sessionStorage
  const [isAdminLoaded, setIsAdminLoaded] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const savedAdmin = sessionStorage.getItem("admin");
    const savedVolunteer = sessionStorage.getItem("volunteerData");

    if (savedAdmin) {
      const adminData = JSON.parse(savedAdmin);
      setAdmin(adminData);
      setAccessToken(adminData.accessToken);
      setAdminId(adminData.userId);
    } else if (savedVolunteer) {
      const volunteerData = JSON.parse(savedVolunteer);
      setAdmin(null);
      setAccessToken(volunteerData.accessToken);
      setAdminId(volunteerData.userId);
    }

    setIsAdminLoaded(true);
  }, []);

  useEffect(() => {
    if (!IsUnauthorised) return;

    const isVolunteerRoute = window.location.pathname.startsWith("/volunteer");

    if (isVolunteerRoute) {
      sessionStorage.removeItem("volunteerData");
      sessionStorage.removeItem("volunteerToken");
      sessionStorage.removeItem("volunteer");
      setAccessToken(null);
      setAdminId(null);
      navigate("/volunteer/login", { replace: true });
    } else {
      sessionStorage.removeItem("admin");
      setAdmin(null);
      setAccessToken(null);
      setAdminId(null);
      navigate("/login", { replace: true });
    }

    setIsUnauthorised(false);
  }, [IsUnauthorised, navigate]);

  useEffect(() => {
    const handleStorageChange = () => {
      const savedAdmin = sessionStorage.getItem("admin");
      const savedVolunteer = sessionStorage.getItem("volunteerData");

      if (savedAdmin) {
        const adminData = JSON.parse(savedAdmin);
        setAdmin(adminData);
        setAccessToken(adminData.accessToken);
        setAdminId(adminData.userId);
      } else if (savedVolunteer) {
        const volunteerData = JSON.parse(savedVolunteer);
        setAdmin(null);
        setAccessToken(volunteerData.accessToken);
        setAdminId(volunteerData.userId);
      } else {
        setAdmin(null);
        setAccessToken(null);
        setAdminId(null);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const token = btoa(`${username}:${password}`);

  const authHeaders = useMemo(() => {
    return {
      accept: "*/*",
      "Content-Type": "application/json",
      Authorization: `Basic ${token}`,
      "X-App-Name": appName || "WebApp",
    };
  }, [token, appName]);

  const bearerAuthHeaders = useMemo(() => {
    return {
      accept: "*/*",
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      "X-App-Name": appName || "WebApp",
    };
  }, [accessToken, appName]);

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: baseurl,
      headers: authHeaders,
    });

    instance.interceptors.request.use(
      (config) => {
        if (AdminId) {
          config.headers["ADMIN-ID"] = String(AdminId);
          config.headers["X-Admin-Id"] = String(AdminId);
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    instance.interceptors.response.use(
      (response) => {
        handleApiResponseCode(response?.data);
        return response;
      },
      (error) => {
        const status = error?.response?.status;

        if (status === 401) {
          setIsUnauthorised(true);
          toast.error("Session expired. Please log in again.");
        }

        return Promise.reject(error);
      },
    );

    return instance;
  }, [baseurl, authHeaders, AdminId, handleApiResponseCode, setIsUnauthorised]);

  const bearerApi = useMemo(() => {
    const instance = axios.create({
      baseURL: baseurl,
      headers: bearerAuthHeaders,
    });

    instance.interceptors.request.use(
      (config) => {
        if (AdminId) {
          config.headers["ADMIN-ID"] = String(AdminId);
          config.headers["X-Admin-Id"] = String(AdminId);
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    instance.interceptors.response.use(
      (response) => {
        handleApiResponseCode(response?.data);
        return response;
      },
      (error) => {
        const status = error?.response?.status;

        if (status === 401) {
          setIsUnauthorised(true);
          toast.error("Session expired. Please log in again.");
        }

        return Promise.reject(error);
      },
    );

    return instance;
  }, [
    baseurl,
    bearerAuthHeaders,
    AdminId,
    handleApiResponseCode,
    setIsUnauthorised,
  ]);

  const bumppyApi = useMemo(() => {
  const instance = axios.create({
    baseURL: packkyBumppyBaseUrl,

    auth: {
      username: bumppyUsername,
      password: bumppyPassword,
    },

    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });

  return instance;
}, [
  packkyBumppyBaseUrl,
  bumppyUsername,
  bumppyPassword,
]);

  const [searchQueryGlobal, setSearchQueryGlobal] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [socketData, setSocketData] = useState(null);
  const socketRef = useRef(null);
  const reconnectTimeout = useRef(null);

  const logoutAdmin = () => {
    sessionStorage.removeItem("admin");
    setAdmin(null);
    setAccessToken(null);
    setAdminId(null);
  };

  const logoutVolunteer = () => {
    sessionStorage.removeItem("volunteerData");
    sessionStorage.removeItem("volunteerToken");
    sessionStorage.removeItem("volunteer");
    setAccessToken(null);
    setAdminId(null);
  };

  // New task web socket Auto-Reconnecting WebSocket
  useEffect(() => {
    if (!admin?.userId) return;

    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;

    const connectWebSocket = () => {
      const ws = new WebSocket(
        `wss://api2.packky.com/ws?userId=${admin.userId}&roomId=task_room_v1`,
      );
      socketRef.current = ws;

ws.onopen = () => {
  console.log(" Task WebSocket connected", {
    userId: admin.userId,
    roomId: "task_room_v1",
    readyState: ws.readyState,
    time: new Date().toLocaleString(),
  });

  reconnectAttempts = 0;
};

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log(" Socket Message:", message);
          setSocketData(message);
          setApiCall((prev) => !prev);

          // Play notification sound only if not muted
          if (!isMutedRef.current && audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch((err) => {
              console.warn(
                "Notification sound blocked until user interacts",
                err,
              );
            });
          }
        } catch (e) {
          console.error(" Failed to parse socket message", e);
        }
      };

      ws.onerror = (err) => {
  console.error(" Task WebSocket error", {
    userId: admin.userId,
    roomId: "task_room_v1",
    error: err,
    readyState: ws.readyState,
    time: new Date().toLocaleString(),
  });

  ws.close();
};

     ws.onclose = (event) => {
  console.warn(" Task WebSocket disconnected", {
    userId: admin.userId,
    roomId: "task_room_v1",
    code: event?.code,
    reason: event?.reason || "No reason provided",
    wasClean: event?.wasClean,
    reconnectAttempts,
    time: new Date().toLocaleString(),
  });

  if (reconnectAttempts < maxReconnectAttempts) {
    const delay = Math.min(1000 * 2 ** reconnectAttempts, 10000);

    console.log(" Task WebSocket reconnect scheduled", {
      userId: admin.userId,
      roomId: "task_room_v1",
      attempt: reconnectAttempts + 1,
      maxReconnectAttempts,
      delayInSeconds: delay / 1000,
      time: new Date().toLocaleString(),
    });

    reconnectTimeout.current = setTimeout(connectWebSocket, delay);
    reconnectAttempts++;
  } else {
    console.error(" Task WebSocket max reconnect attempts reached", {
      userId: admin.userId,
      roomId: "task_room_v1",
      maxReconnectAttempts,
      time: new Date().toLocaleString(),
    });
  }
}; ws.onclose = () => {
        console.warn(" WebSocket disconnected");

        if (reconnectAttempts < maxReconnectAttempts) {
          const delay = Math.min(1000 * 2 ** reconnectAttempts, 10000); // Exponential backoff
          console.log(` Reconnecting in ${delay / 1000}s...`);
          reconnectTimeout.current = setTimeout(connectWebSocket, delay);
          reconnectAttempts++;
        } else {
          console.error(" Max reconnect attempts reached.");
        }
      };
    };

    connectWebSocket();

    return () => {
  console.log(" Task WebSocket cleanup/disconnect called", {
    userId: admin.userId,
    roomId: "task_room_v1",
    time: new Date().toLocaleString(),
  });

  if (socketRef.current) {
    socketRef.current.close(1000, "Component unmounted or user changed");
  }

  clearTimeout(reconnectTimeout.current);
};
 }, [admin?.userId]);

  // Notifications Web Socket
  const [notificationCount, setNotificationCount] = useState(() => {
    const saved = sessionStorage.getItem("notificationCount");
    return saved ? Number(saved) : 0;
  });

  const [notificationRefreshKey, setNotificationRefreshKey] = useState(0);
  const [notificationSocketData, setNotificationSocketData] = useState(null);
  const [shops, setShops] = useState([]);
  const notificationSocketRef = useRef(null);
  const notificationReconnectTimeoutRef = useRef(null);

  const incrementNotificationCount = () => {
    setNotificationCount((prev) => {
      const next = prev + 1;
      sessionStorage.setItem("notificationCount", String(next));
      return next;
    });
  };

  const clearNotificationCount = () => {
    setNotificationCount(0);
    sessionStorage.setItem("notificationCount", "0");
  };

  const triggerNotificationRefresh = () => {
    setNotificationRefreshKey((prev) => prev + 1);
  };
  useEffect(() => {
    const handleRouteBasedNotificationReset = () => {
      if (window.location.pathname === "/notifications") {
        clearNotificationCount();
      }
    };

    handleRouteBasedNotificationReset();

    window.addEventListener("popstate", handleRouteBasedNotificationReset);

    return () => {
      window.removeEventListener("popstate", handleRouteBasedNotificationReset);
    };
  }, []);

  useEffect(() => {
    if (!admin?.userId) return;

    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;

    const connectNotificationWebSocket = () => {
      const ws = new WebSocket(
        `wss://api2.packky.com/ws?userId=${admin.userId}&roomId=task_room_v2`,
      );

      notificationSocketRef.current = ws;

    ws.onopen = () => {
  console.log(" Notification WebSocket connected", {
    userId: admin.userId,
    roomId: "task_room_v2",
    readyState: ws.readyState,
    time: new Date().toLocaleString(),
  });

  reconnectAttempts = 0;
};

      ws.onmessage = (event) => {
        try {
          let parsedMessage = event.data;

          try {
            parsedMessage = JSON.parse(event.data);
          } catch {
            parsedMessage = { message: event.data };
          }

          console.log("Notification Socket Message:", parsedMessage);
          setNotificationSocketData(parsedMessage);

          const rawMessage =
            typeof parsedMessage === "string"
              ? parsedMessage
              : parsedMessage?.Message ||
                parsedMessage?.message ||
                parsedMessage?.body ||
                "";

          const isOffDutyNotification =
            rawMessage?.toLowerCase()?.includes("requested to go offline") ||
            rawMessage
              ?.toLowerCase()
              ?.includes("driver has requested to go offline");

          if (isOffDutyNotification) {
            const isOnNotificationRoute =
              window.location.pathname === "/notifications";

            if (isOnNotificationRoute) {
              triggerNotificationRefresh();
            } else {
              incrementNotificationCount();
            }

            if (!isMutedRef.current && audioRef.current) {
              audioRef.current.currentTime = 0;
              audioRef.current.play().catch((err) => {
                console.warn(
                  "Notification sound blocked until user interacts",
                  err,
                );
              });
            }
          }
        } catch (e) {
          console.error("Failed to parse notification socket message", e);
        }
      };

     ws.onerror = (err) => {
  console.error(" Notification WebSocket error", {
    userId: admin.userId,
    roomId: "task_room_v2",
    error: err,
    readyState: ws.readyState,
    time: new Date().toLocaleString(),
  });

  ws.close();
};

   ws.onclose = (event) => {
  console.warn(" Notification WebSocket disconnected", {
    userId: admin.userId,
    roomId: "task_room_v2",
    code: event?.code,
    reason: event?.reason || "No reason provided",
    wasClean: event?.wasClean,
    reconnectAttempts,
    time: new Date().toLocaleString(),
  });

  if (reconnectAttempts < maxReconnectAttempts) {
    const delay = Math.min(1000 * 2 ** reconnectAttempts, 10000);

    console.log(" Notification WebSocket reconnect scheduled", {
      userId: admin.userId,
      roomId: "task_room_v2",
      attempt: reconnectAttempts + 1,
      maxReconnectAttempts,
      delayInSeconds: delay / 1000,
      time: new Date().toLocaleString(),
    });

    notificationReconnectTimeoutRef.current = setTimeout(
      connectNotificationWebSocket,
      delay,
    );

    reconnectAttempts++;
  } else {
    console.error(" Notification WebSocket max reconnect attempts reached", {
      userId: admin.userId,
      roomId: "task_room_v2",
      maxReconnectAttempts,
      time: new Date().toLocaleString(),
    });
  }
};
    };

    connectNotificationWebSocket();

    return () => {
  console.log(" Notification WebSocket cleanup/disconnect called", {
    userId: admin.userId,
    roomId: "task_room_v2",
    time: new Date().toLocaleString(),
  });

  if (notificationSocketRef.current) {
    notificationSocketRef.current.close(
      1000,
      "Component unmounted or user changed",
    );
  }

  clearTimeout(notificationReconnectTimeoutRef.current);
};
  }, [admin?.userId]);

  return (
    <GlobalContext.Provider
      value={{
        baseurl,
        appName,
        token,
        api,
        authHeaders,
        bearerApi,
        bearerAuthHeaders,
bumppyApi,
packkyBumppyBaseUrl,
        handleApiResponseCode,
        searchQueryGlobal,
        setSearchQueryGlobal,
        accessToken,
        setAccessToken,
        ApiCall,
        setApiCall,
        setIsUnauthorised,
        IsUnauthorised,
        socketData,
        AdminId,
        setAdminId,
        isMuted,
        toggleMute,
        showOld,
        setShowOld,
        admin,
        setAdmin,
        drivers,

        //notifications values

        notificationCount,
        setNotificationCount,
        clearNotificationCount,
        incrementNotificationCount,
        notificationRefreshKey,
        triggerNotificationRefresh,
        notificationSocketData,

        setDrivers,
        logoutAdmin,
        logoutVolunteer,
        isAddTaskOpen,
        setIsAddTaskOpen,
        shops,
        setShops,

        apiGraphqlUrl,
        GraphToken,

        isAccessDenied,
        setIsAccessDenied,
        accessDeniedMessage,
        setAccessDeniedMessage,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
}

export function useGlobalContext() {
  return useContext(GlobalContext);
}
